///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { GtthHistoryService } from '../../../logical-topology/engine/history-service';
import { ExtendedSectorData } from '../p2mp-info/extended-sector-data.model';
import { ExtendedLinkData } from '../p2p-info/extended-link-data.model';
import { AbstractInteractiveWindow } from '../../abstract-interactive-window';
import { ProjectInteractiveWindowsPopupService } from '../../project-interactive-windows-popup.service';
import { scrollToEl } from '../../../../shared/utils';
import { PARAM_BBOX } from '../../../../shared/constants/url-params.constants';
import { ActivatedRoute } from '@angular/router';
import { LinkFeature } from '../../../logical-topology/entities/link/link-feature';
import { MatDialogRef } from '@angular/material/dialog';
import { ProjectProductsService } from '../../../logical-topology/engine/project-products-service';
import { isDefined } from '@siklu/sui-lib/utils';

@Component({
    selector: 'winde-report-overloaded-sectors',
    templateUrl: './report-overloaded-sectors.component.html',
    styleUrls: ['./report-overloaded-sectors.component.scss', '../../project-windows.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class GtthReportOverloadedSectorsComponent extends AbstractInteractiveWindow implements OnInit {
    public reportData: ExtendedSectorData[];
    public refreshing = false;
    public selectedSector: ExtendedSectorData;

    @ViewChild('scrollContainer') scrollContainer: ElementRef;

    constructor(
        private readonly elementRef: ElementRef,
        public dialogRef: MatDialogRef<GtthReportOverloadedSectorsComponent>,
        public historyService: GtthHistoryService,
        private readonly changeDetector: ChangeDetectorRef,
        private readonly projectProductsService: ProjectProductsService
    ) {
        super(elementRef);
    }

    ngOnInit() {
        this.refreshReport();
    }

    refreshReport() {
        this.refreshing = true;

        this.reportData = [];

        const linksIterator = this.historyService.logicalTopology.getLinksIterator();

        const sectorsMap = new Map<number, LinkFeature[][]>();

        for (const link of linksIterator) {
            if (
                link.getSectorIndex() === -1 ||
                !this.projectProductsService.isDeviceHasSectors(link.getBuUsedProduct()) ||
                !link.isUserForced()
            ) {
                continue;
            }

            if (!sectorsMap.has(link.getSectorIndex())) {
                sectorsMap.set(link.getSectorIndex(), [[], [], [], []]);
            }

            sectorsMap.get(link.getSectorIndex())[link.getSectorInDevice()].push(link);
        }

        sectorsMap.forEach((secotrsInDevice, sectorId) => {
            secotrsInDevice.forEach((links, index) => {
                if (links.length == 0) {
                    return;
                }

                const device = this.projectProductsService.getBaseRadioById(links[0].getBuUsedProduct());
                if (links.length < device.pdbConfig.sectorConfig[index].maxTu) {
                    return;
                }

                this.reportData.push({
                    sectIdx: sectorId,
                    sectIndev: index + 1,
                    nodeid: links[0].getSectorStationName(),
                    sectorLinkData: links.map((linkDto) => {
                        return {
                            node0Id: linkDto.getFromStation().getName(),
                            node1Id: linkDto.getToStation().getName(),
                            desc: linkDto.getFromStation().getName() + ' - ' + linkDto.getToStation().getName(),
                            distance: Math.round(linkDto.getDistance()),
                            isUserForced: linkDto.isUserForced(),
                            isInstalled: linkDto.isInstalled()
                        };
                    })
                } as ExtendedSectorData);
            });
        });

        this.refreshing = false;
    }

    linkFrozen(fromName: string, toName: string, isForced: boolean) {
        this.changeLinkFrozenStatus(fromName, toName, isForced);
    }

    linkInstalled(fromName: string, toName: string, isInstalled: boolean) {
        this.changeLinkFrozenStatus(fromName, toName, undefined, isInstalled);
    }

    sectorSelected(sectorIndex: number, selected: boolean) {
        if (!selected) {
            this.selectedSector = undefined;
        } else if (this.selectedSector && this.selectedSector.sectIdx === sectorIndex) {
            return;
        } else {
            this.selectedSector = this.reportData.find((sectorData: ExtendedSectorData) => {
                return sectorData.sectIdx === sectorIndex;
            });
        }

        if (!this.selectedSector) {
            return;
        }

        this.scrollToSector(sectorIndex);

        this.changeDetector.markForCheck();
    }

    sectorDirection(sectorIndex: number, angle: number) {
        this.sectorSelected(sectorIndex, true);
        if (!this.selectedSector) {
            return;
        }
        this.selectedSector.sectDir = angle;
        this.changeDetector.markForCheck();
    }

    closeModal() {
        this.reportData = undefined;
        this.dialogRef.close('cancel');
    }

    trackSector(_index, item: ExtendedSectorData) {
        return item.sectIdx;
    }

    trackLinkData(_index, item: ExtendedLinkData) {
        return item.desc;
    }

    @HostListener('document:keydown', ['$event'])
    onKeydown(event: KeyboardEvent) {
        if (event.key === 'Escape' && !this.historyService.hasSearchMarkers()) {
            this.closeModal();
        }
    }

    designRequired(): boolean {
        return false;
    }

    isRestorable(): boolean {
        return true;
    }

    private changeLinkFrozenStatus(fromName: string, toName: string, isForced: boolean, isInstalled?: boolean) {
        const sector = this.reportData.find((sectorData: ExtendedSectorData) => {
            if (sectorData.nodeid === fromName || sectorData.nodeid === toName) {
                const link = sectorData.sectorLinkData.find((linkData: ExtendedLinkData) => {
                    return (
                        (linkData.node0Id === fromName || linkData.node1Id === fromName) &&
                        (linkData.node0Id === toName || linkData.node1Id === toName)
                    );
                });

                if (link) {
                    link.isUserForced = isDefined(isForced) ? isForced : link.isUserForced;
                    link.isInstalled = isDefined(isInstalled) ? isInstalled : link.isInstalled;
                    this.changeDetector.markForCheck();
                    return true;
                } else {
                    return false;
                }
            } else {
                return false;
            }
        });

        if (sector) {
            this.scrollToSector(sector.sectIdx);
        }
    }

    private scrollToSector(sectorIndex: number) {
        if (this.scrollContainer) {
            scrollToEl(this.scrollContainer.nativeElement, `.sector--${sectorIndex}`);
        }
    }
}

@Component({
    selector: 'winde-report-overloaded-sectors-popup',
    template: '',
    standalone: false
})
export class GtthReportOverloadedSectorsPopupComponent implements OnInit {
    constructor(
        private readonly interactiveWindowsPopupService: ProjectInteractiveWindowsPopupService,
        private readonly activatedRoute: ActivatedRoute
    ) {}

    ngOnInit() {
        let bboxParams;
        if (this.activatedRoute.snapshot.queryParamMap.has(PARAM_BBOX)) {
            bboxParams = this.activatedRoute.snapshot.queryParamMap
                .get(PARAM_BBOX)
                .split(',')
                .map((value) => parseFloat(value));
        }
        const routerPath = this.activatedRoute.routeConfig.path;
        this.interactiveWindowsPopupService.openInteractiveWindow(
            GtthReportOverloadedSectorsComponent,
            ProjectInteractiveWindowsPopupService.OVERLOADED_SECOTRS_REPORT,
            routerPath,
            bboxParams
        );
    }
}
